import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import LoadThemeComponent from '../../../base/LoadThemeComponent';
import { GameType, LineNumbers, PickedNumber, SelectSignalPick} from '../Types/Types';
import DrawCounter from '../elements/DrawCounter';
import HiddenInputs from '../elements/HiddenInputs';
import Purchase from '../elements/Purchase';
import PurchaseConfirmation from '../elements/PurchaseConfirmation';
import ResellerModule from '../elements/ResellerModule';
import SelectableLotteryDrawDatesModule from '../elements/SelectableLotteryDrawDatesModule';
import WinUpTo from '../elements/WinUpTo';
import { supportStakes } from '../helpers/pickHelper';
import { RootState } from '../redux/store';
import { setGameType, setLines, setMultiplier, setPickedNumbers, setStakes } from '../redux/storeSlice';

import SelectedLines from './basket/SelectedLines';
import GameTypeSelect from './options/GameTypeSelect';
import MultiplierSelect from './options/MultiplierSelect';
import NumberOfDrawsSelect from './options/NumberOfDrawsSelect';
import PickNLinesTitle from './picker/PickNLinesTitle';
import Picker from './picker/Picker';

import PurchasePromoCode from 'components/promo-codes/PurchasePromoCode';
import { isPremierDubai, isThankYouLotto } from 'utils/template';

function PickPlay() {
    const dispatch = useDispatch();

    const {
        lines,
        lottery,
        displayDrawCounter,
        gameType,
        gameTypes,
        stakeMin,
        stakeMax,
        multiplier,
        stakes,
        resellerOptionsEnabled,
        pickedNumbers,
        selectableDrawDateTimes,
    } = useSelector((state: RootState) => state.playStore);

    const [selectSignal, setSelectSignal] = useState<SelectSignalPick>({ clickSource: 'homepage', lineIndex: 0 });

    const isTemplateThankYouLotto = useMemo(() => isThankYouLotto(), []);

    const availablePurchaseOptions = [
        {
            key: 'gameType',
            component: (
                <GameTypeSelect
                    gameTypes={gameTypes}
                    gameType={gameType}
                    setGameType={(value: GameType) => dispatch(setGameType(value))}
                    useCounterClass={displayDrawCounter}
                    setLines={(value: LineNumbers[]) => dispatch(setLines(value))}
                    setPickedNumbers={(value: PickedNumber[]) => dispatch(setPickedNumbers(value))}
                    setStakes={(value: number[]) => dispatch(setStakes(value))}
                />
            ),
            isVisible: true,
        },
        {
            key: 'multiplier',
            component: (
                <MultiplierSelect
                    multipliers={gameType.distinct_multipliers}
                    multiplier={multiplier}
                    setMultiplier={(value: number) => dispatch(setMultiplier(value))}
                    useCounterClass={displayDrawCounter}
                />
            ),
            isVisible: !supportStakes(stakeMin, stakeMax) && lottery.lotteryRule.multipliers_visible,
        },
        {
            key: 'selectableDrawDates',
            component: <SelectableLotteryDrawDatesModule />,
            isVisible: selectableDrawDateTimes.length,
        },
        {
            key: 'resellerModule',
            component: <ResellerModule />,
            isVisible: resellerOptionsEnabled,
        },
        {
            key: 'numberOfDraws',
            component: <NumberOfDrawsSelect />,
            isVisible: lottery.isMultiDrawEnabled,
        },
    ].filter((option) => option.isVisible);

    function getPurchaseOptions() {
        return (
            <div
                id="purchase-options"
                className="w-100 purchase-options purchase-options-pick user-guide-play-ticket-type"
            >
                <div className="row">
                    <div className="col-12 col-md-6 pr-md-2 col-lg-4 pl-lg-3 pr-lg-0 order-lg-last purchase-opt-col">
                        <DrawCounter />
                    </div>

                    <GameTypeSelect
                        gameTypes={gameTypes}
                        gameType={gameType}
                        setGameType={(value: GameType) => dispatch(setGameType(value))}
                        useCounterClass={displayDrawCounter}
                        setLines={(value: LineNumbers[]) => dispatch(setLines(value))}
                        setPickedNumbers={(value: PickedNumber[]) => dispatch(setPickedNumbers(value))}
                        setStakes={(value: number[]) => dispatch(setStakes(value))}
                    />

                    {!supportStakes(stakeMin, stakeMax) && lottery.lotteryRule.multipliers_visible ? (
                        <MultiplierSelect
                            multipliers={gameType.distinct_multipliers}
                            multiplier={multiplier}
                            setMultiplier={(value: number) => dispatch(setMultiplier(value))}
                            useCounterClass={displayDrawCounter}
                        />
                    ) : null}

                    {lottery.isMultiDrawEnabled && (
                        <div
                            className={`col-12 ${
                                displayDrawCounter ? 'col-md-6 pl-md-3' : ''
                            } col-lg pl-lg-3 pr-lg-2 purchase-opt-col`}
                        >
                            <NumberOfDrawsSelect />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    function getDefaultPurchaseOptions() {
        return (
            <div
                id="purchase-options"
                className="w-100 purchase-options purchase-options-pick user-guide-play-ticket-type"
            >
                <div className="row">
                    {availablePurchaseOptions.map((option, index) => {
                        const isLastSingleItem = index === availablePurchaseOptions.length - 1 && index % 2 === 0;
                        return (
                            <div key={option.key} className={`col-${isLastSingleItem ? '12' : '6 mb-2'}`}>
                                {option.component}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    const baseView = () => (
        <>
            <div className="row row-picker no-gutters d-flex w-100 pick-wrapper">
                <div className="purchase w-100 play-pick-type user-guide-play-picker" id="purchase">
                    <div className="container play">
                        <div className="myaccount play-main play-lottery row">
                            {!isPremierDubai() && (
                                <div className="col-12 mb-md-5 mb-4 px-0">
                                    <WinUpTo />
                                </div>
                            )}

                            <SelectedLines numbersToPick={gameType.numbers_to_pick} />

                            <div className="container">
                                <PickNLinesTitle numbersToPick={gameType.numbers_to_pick} />
                                <Picker
                                    ranges={lottery.lotteryRule.ranges}
                                    numbersToPick={gameType.numbers_to_pick}
                                    maxLinesPerTicket={lottery.maxLinesPerTicket}
                                    pickedNumbers={pickedNumbers}
                                    setPickedNumbers={(value: PickedNumber[]) => dispatch(setPickedNumbers(value))}
                                    selectSignal={selectSignal}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {getPurchaseOptions()}
            <PurchasePromoCode />

            <div
                className={`row no-gutters w-100 ${
                    resellerOptionsEnabled ? 'd-block d-sm-flex justify-content-between' : 'justify-content-sm-between'
                } align-items-center`}
            >
                <SelectableLotteryDrawDatesModule />
                <ResellerModule />
                <Purchase />
            </div>
        </>
    );

    const defaultView = () => (
        <>
            <div className="row-picker g-0 d-flex pick-wrapper">
                <div className="purchase play-pick-type user-guide-play-picker w-100" id="purchase">
                    <div className="container play">
                        <div className="myaccount play-main play-lottery row">
                            <SelectedLines numbersToPick={gameType.numbers_to_pick} setSelectSignal={setSelectSignal} />

                            <Picker
                                ranges={lottery.lotteryRule.ranges}
                                numbersToPick={gameType.numbers_to_pick}
                                maxLinesPerTicket={lottery.maxLinesPerTicket}
                                pickedNumbers={pickedNumbers}
                                setPickedNumbers={(value: PickedNumber[]) => dispatch(setPickedNumbers(value))}
                                selectSignal={selectSignal}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {getDefaultPurchaseOptions()}
            <Purchase />
            {/*
            <PurchasePromoCode />
            */}
        </>
    );

    return (
        <>
            <PurchaseConfirmation />
            <HiddenInputs lines={lines} stakes={stakes} />
            <LoadThemeComponent defaultView={defaultView} baseView={baseView} />
        </>
    );
}

export default PickPlay;
